import {useEffect} from 'react';
import {
  calcFieldInsets,
  FIELD_INSETS_MEASURE_EVENT,
  FIELD_INSET_ATTRIBUTE,
  FIELD_INSET_MIN_WIDTH_ATTRIBUTE,
  resetFieldInsets,
  setFieldInsets,
} from '../../utils/visuals/fieldInsets';

const MARKED_SELECTOR = `[${FIELD_INSET_ATTRIBUTE}]`;

/**
 * Замер помеченных элементов интерфейса в отступы свободной области
 * @param {ParentNode} root
 * @param {{width: number, height: number}} viewport
 */
export const measureFieldInsets = (root, viewport) => {
  const entries = [];
  root.querySelectorAll(MARKED_SELECTOR).forEach((element) => {
    const minWidth = Number(element.getAttribute(FIELD_INSET_MIN_WIDTH_ATTRIBUTE));
    if (minWidth > 0 && viewport.width < minWidth) {
      return;
    }
    entries.push({side: element.getAttribute(FIELD_INSET_ATTRIBUTE), rect: element.getBoundingClientRect()});
  });
  return calcFieldInsets(entries, viewport);
};

/**
 * Следит за элементами HUD с data-field-inset и публикует отступы свободной
 * области поля (см. utils/visuals/fieldInsets). Пересчет — на изменение
 * размеров помеченных элементов (ResizeObserver), на появление и исчезновение
 * элементов (MutationObserver) и на изменение окна; не чаще раза за кадр
 *
 * @param {boolean} [enabled] — экран матча смонтирован
 */
export const useFieldInsets = (enabled = true) => {
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      return undefined;
    }

    let frameId = null;
    const observed = new Set();
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => scheduleMeasure())
      : null;

    const syncObserved = () => {
      if (!resizeObserver) {
        return;
      }
      const marked = new Set(document.querySelectorAll(MARKED_SELECTOR));
      observed.forEach((element) => {
        if (!marked.has(element)) {
          resizeObserver.unobserve(element);
          observed.delete(element);
        }
      });
      marked.forEach((element) => {
        if (!observed.has(element)) {
          resizeObserver.observe(element);
          observed.add(element);
        }
      });
    };

    const measure = () => {
      frameId = null;
      syncObserved();
      setFieldInsets(measureFieldInsets(document, {width: window.innerWidth, height: window.innerHeight}));
    };

    function scheduleMeasure() {
      if (frameId !== null) {
        return;
      }
      frameId = window.requestAnimationFrame(measure);
    }

    // Набор элементов меняется (полоса этапов войны появляется, футер
    // перестраивается) — ResizeObserver о новых не знает, поэтому смотрим и
    // на дерево. Сам замер дешевый и идет раз за кадр
    const mutationObserver = typeof MutationObserver === 'function'
      ? new MutationObserver(scheduleMeasure)
      : null;
    mutationObserver?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [FIELD_INSET_ATTRIBUTE, FIELD_INSET_MIN_WIDTH_ATTRIBUTE, 'class', 'style'],
    });
    window.addEventListener('resize', scheduleMeasure);
    // Панели выезжают CSS-переходом: смена класса видна сразу, а конечное
    // положение — только когда переход доиграл
    document.addEventListener('transitionend', scheduleMeasure, true);
    document.addEventListener(FIELD_INSETS_MEASURE_EVENT, scheduleMeasure);

    measure();

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      document.removeEventListener('transitionend', scheduleMeasure, true);
      document.removeEventListener(FIELD_INSETS_MEASURE_EVENT, scheduleMeasure);
      resetFieldInsets();
    };
  }, [enabled]);
};
